import React, { useState, useEffect, useRef } from "react";
import "./RecWiseCode.css";
import { useNavigate } from 'react-router-dom';
import CTA from './CTA';

const ServiceAutomationModule = () => {
  const [activeSection, setActiveSection] = useState("hr-helpdesk");
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionsRef = useRef({});
  const navigate = useNavigate();
  const handleServiceAutomation = () => navigate('/access-recruit');

  const sectionContent = {
    "hr-helpdesk": {
      title: "AI-Powered HR Helpdesk",
      description: "Transform HR support with intelligent helpdesk automation powered by artificial intelligence. Provide instant answers to common HR queries through conversational AI chatbots. Automate ticket routing, escalation, and resolution workflows based on query complexity and urgency. Track service level agreements, response times, and resolution rates. Integrate with knowledge bases and document repositories to provide accurate, consistent information to employees 24/7."
    },
    "workflow-automation": {
      title: "Automated Workflow",
      description: "Streamline HR processes with configurable workflow automation for approvals, requests, and notifications. Design custom workflows for employee requests, manager approvals, and cross-departmental processes using drag-and-drop builders. Automate routine tasks like document generation, data updates, and system integrations. Monitor workflow progress in real-time, identify bottlenecks, and optimize processes based on performance analytics and user feedback."
    },
    "self-service-portal": {
      title: "Employee Self-Service Portal",
      description: "Empower employees with comprehensive self-service capabilities through intuitive digital portals. Enable personal information updates, document access, payslip viewing, and benefit enrollment without HR intervention. Provide role-specific dashboards with relevant information and actionable tasks. Support multi-language interfaces and mobile access for convenience. Reduce HR administrative workload while improving employee satisfaction through immediate access to HR services."
    },
    "document-automation": {
      title: "Document Automation",
      description: "Automate document creation, approval, and management throughout the employee lifecycle. Generate personalized documents like offer letters, contracts, policy acknowledgments, and certificates using intelligent templates. Implement electronic signature workflows with automated reminders and tracking. Maintain version control, audit trails, and secure document storage. Integrate with HR systems to automatically populate document fields with accurate employee data."
    },
    "analytics-reporting": {
      title: "Service Analytics & Reporting",
      description: "Gain insights into HR service delivery with comprehensive analytics and real-time reporting. Track key service metrics including request volumes, resolution times, satisfaction scores, and automation rates. Identify common issues, peak service periods, and improvement opportunities. Generate compliance reports, audit trails, and performance dashboards. Use predictive analytics to forecast service demands and optimize resource allocation for efficient HR service delivery."
    }
  };

  const sections = [
    { id: "hr-helpdesk", label: "HR HELPDESK" },
    { id: "workflow-automation", label: "WORKFLOWS" },
    { id: "self-service-portal", label: "SELF-SERVICE" },
    { id: "document-automation", label: "DOCUMENTS" },
    { id: "analytics-reporting", label: "ANALYTICS" }
  ];

  useEffect(() => {
    const onScroll = () => {
      const pos = window.scrollY + 150;
      for (const sec of sections) {
        const el = sectionsRef.current[sec.id];
        if (!el) continue;
        if (pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(sec.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, en.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(sectionsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionsRef.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
  };

  return (
    <div className="resto-main-wrapper">
      {/* <nav className="resto-sticky-navbar">
        <div className="resto-nav-container">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`resto-nav-button ${activeSection === s.id ? "resto-active" : ""}`}
              onClick={() => scrollToSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav> */}

      <div className="resto-content-wrapper">

        {/* 1. HR Helpdesk */}
        <section
          ref={(el) => (sectionsRef.current["hr-helpdesk"] = el)}
          data-section="hr-helpdesk"
          className={`resto-section ${visibleSections.has("hr-helpdesk") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">HR Support</div>
            <h2 className="resto-section-title">
              {sectionContent["hr-helpdesk"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["hr-helpdesk"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleServiceAutomation}>Access Helpdesk</button>
              <button className="resto-secondary-btn" onClick={handleServiceAutomation}>Configure Chatbot</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/sea1.png" className="resto-real-image" alt="HR Helpdesk" />
          </div>
        </section>

        {/* 2. Workflow Automation */}
        <section
          ref={(el) => (sectionsRef.current["workflow-automation"] = el)}
          data-section="workflow-automation"
          className={`resto-section resto-reverse ${visibleSections.has("workflow-automation") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Workflows</div>
            <h2 className="resto-section-title">
              {sectionContent["workflow-automation"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["workflow-automation"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleServiceAutomation}>Design Workflow</button>
              <button className="resto-secondary-btn" onClick={handleServiceAutomation}>Monitor Processes</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/sea2.png" className="resto-real-image" alt="Workflow Automation" />
          </div>
        </section>

        <CTA/>

        {/* 3. Self-Service Portal */}
        <section
          ref={(el) => (sectionsRef.current["self-service-portal"] = el)}
          data-section="self-service-portal"
          className={`resto-section ${visibleSections.has("self-service-portal") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Self-Service</div>
            <h2 className="resto-section-title">
              {sectionContent["self-service-portal"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["self-service-portal"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleServiceAutomation}>Access Portal</button>
              <button className="resto-secondary-btn" onClick={handleServiceAutomation}>Customize Dashboard</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/sea3.png" className="resto-real-image" alt="Self-Service Portal" />
          </div>
        </section>

        {/* 4. Document Automation */}
        <section
          ref={(el) => (sectionsRef.current["document-automation"] = el)}
          data-section="document-automation"
          className={`resto-section resto-reverse ${visibleSections.has("document-automation") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Documents</div>
            <h2 className="resto-section-title">
              {sectionContent["document-automation"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["document-automation"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleServiceAutomation}>Generate Documents</button>
              <button className="resto-secondary-btn" onClick={handleServiceAutomation}>Track Signatures</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/sea4.png" className="resto-real-image" alt="Document Automation" />
          </div>
        </section>

        {/* 5. Analytics & Reporting */}
        <section
          ref={(el) => (sectionsRef.current["analytics-reporting"] = el)}
          data-section="analytics-reporting"
          className={`resto-section ${visibleSections.has("analytics-reporting") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Analytics</div>
            <h2 className="resto-section-title">
              {sectionContent["analytics-reporting"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["analytics-reporting"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleServiceAutomation}>View Analytics</button>
              <button className="resto-secondary-btn" onClick={handleServiceAutomation}>Generate Reports</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/sea5.png" className="resto-real-image" alt="Service Analytics" />
          </div>
        </section>

      </div>
    </div>
  );
};

export default ServiceAutomationModule;